export type InputRow = {
  /** Which vessel this row fills or draws from, when any. Written by
   *  `scheduler_api` from the engine's `ScheduleRow::tank_id`.
   *
   *  NOT the same question as `isTankTransfer`, which asks whether one of the
   *  row's two ASSETS is a vessel. A `Tank -> Kitchen2 -> Line` row has two
   *  real assets — so the flag is false — while still drawing its input from a
   *  tank. That is the case the Gantt needs this field for. */
  tankId?: string | null
  /** True when the vessel occupies one of the two asset slots, i.e. the row is
   *  "half real". False on a `Tank -> Kitchen2 -> Line` row. */
  isTankTransfer?: boolean
  // Present on rows from a multi-week consolidated run's combined output
  // (tags which week the row's activity starts in); absent for single-week
  // schedules.
  weekNumber?: number
  kitchen: string
  kitchenCIP: string
  kitchenCleanDuration: number
  kitchenDuration: number
  kitchen_clean_end_time: number
  kitchen_clean_start: number
  kitchen_clean_start_time: number
  kitchen_end_time: number
  kitchen_start_time: number
  // Kitchen preclean fields
  kitchen_preclean_start_time: number
  kitchen_preclean_end_time: number
  kitchenPrecleanCIP: string
  line: string
  lineCIP: string
  lineCleanDuration: number
  lineDuration: number
  line_clean_end_time: number
  line_clean_start: number
  line_clean_start_time: number
  line_end_time: number
  line_start_time: number
  // Line preclean fields
  line_preclean_start_time: number
  line_preclean_end_time: number
  linePrecleanCIP: string
  // Additional CIPs (JSON arrays with all cleaning events)
  additional_line_cips?: string | null
  additional_kitchen_cips?: string | null
  // Primary (scalar) changeover fields — one event per row
  line_changeover_start_time?: number | null
  line_changeover_end_time?: number | null
  kitchen_changeover_start_time?: number | null
  kitchen_changeover_end_time?: number | null
  // Additional changeover-asset activity (JSON array, shape: {start, end, changeover_asset?, asset_id?, reason?})
  additional_line_changeovers?: string | null
  additional_kitchen_changeovers?: string | null
  base_material: string
  base_material_description: string
  productCode: string
  production_description: string
  pr: string
  /** True when this row is REPLAYED HISTORY from a mid-week reschedule — it
   *  ran before the cut-off and was copied into the result verbatim rather
   *  than planned by the optimizer. `scheduler_api`'s `extract_fixed_rows`
   *  sets it; every newly optimised row carries `false`
   *  (`postprocess::EnrichedRow`), as does every row of an ordinary run.
   *
   *  So `fixed === true` appearing anywhere in a result is itself the tell
   *  that the result IS a reschedule — which is what the timeline's
   *  rescheduled/already-run cues key off. Typed required because the column
   *  has always been written, but test it as `=== true`: output written
   *  before the column existed reads back without it. */
  fixed: boolean
  site_location: string
  quantity: number
  runRateBase: number
  runRateLine: number
  comments: string
  product_description: string
  id: number
  resource_requirement_kitchen: {
    [key: string]: {
      fixed: boolean
      runRate: string
    }
  }
  resource_requirement_line: {
    [key: string]: {
      fixed: boolean
      runRate: string
    }
  }
}

export type KitchenSchedule = InputRow & {
  blocks: {
    start_time: number
    end_time: number
    base_material: string
  }[]
}

export type KitchenScheduleMap = Record<string, KitchenSchedule>

export type LineSchedule = InputRow & {
  blocks: {
    start_time: number
    end_time: number
    productCode: string
  }[]
}

export type LineScheduleMap = Record<string, LineSchedule>

export type CleaningSchedule = {
  cip: string
  blocks: {
    start_time: number
    end_time: number
  }[]
}

export type CleanScheduleMap = Record<string, CleaningSchedule>

export type SchedulerTableProps = {
  data: InputRow[]
}

export const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const generateTimeSlots = () => {
  const timeSlots = []
  for (let hour = 0; hour < 24; hour++) {
    const formattedHour = `${hour < 10 ? '0' : ''}${hour}:00`
    timeSlots.push(formattedHour)
  }
  return timeSlots
} // 00:00 --> 23:00

export const generateHeaders = (time_slots: string[]): string[] => {
  return daysOfWeek.flatMap((day) => time_slots.map((time) => `${day}-${time}`))
}

export type parseTimeIntervalsReturnType = {
  start_time: number
  end_time: number
}
export type AdditionalSlotsType = {
  downTime: string
  id: number
  resource: string
  resourceType: string
  site_location: string
}

export type columnGroupingModelType = {
  groupId: string
  headerName: string
  children: {
    field: string
    headerName: string
    width: number
    headerClassName: string
    renderHeader: () => JSX.Element
  }[]
}

export type KitchenRow = {
  base_material: string[]
  base_material_description: string[]
  lines: string[]
  value: number
  kitchen_start_time: number
  kitchen_end_time: number
  down_time_start_time?: number
  down_time_end_time?: number
  slot_types: string[]
  cleaning_start_time: number
  cleaning_end_time: number
  preclean_start_time: number
  preclean_end_time: number
  changeover_start_time: number
  changeover_end_time: number
  /** Every changeover event touching this hour, described. The scalar pair
   *  above carries only the window; this is what lets the popper say what the
   *  change is FOR. Optional because rows built before this existed (and the
   *  shared `addAdditionalSlots` default) omit it. */
  changeover_details?: ChangeoverDetail[]
}

// const kobj = {
//   CP1: {
//     cip: '',
//     runRateBase: 0,
//     resources: {
//       '1': {
//         'hour-0': {},
//         'hour-1': {},
//         'hour-2': {},
//         'hour-3': {},
//       },
//       DT1: {
//         'hour-0': {},
//         'hour-1': {},
//         'hour-2': {},
//         'hour-3': {},
//       },
//     },
//   },
// }

export type kTypeCleaning<T> = {
  [key: string]: {
    cip: string
    runRateBase: number
    resources: {
      [key: string]: {
        [key: string]: T | string
      }
    }
  }
}

export type kType<T> = {
  [key: string]: {
    [key: string]: T | string | number
  }
}

export type OutputPopperProps = {
  rowName: string
  tableType: string
  start_time: string
  end_time: string
  base_material_description: string[]
  slot_types: string[]
  crossReference?: string[]
  /** Changeover view only: what the change actually is. */
  changeoverDetails?: ChangeoverDetail[]

  open: boolean
  anchorPosition: { top: number; left: number }
  handleClose: () => void
  anchorEl: HTMLButtonElement | null
  id: string | undefined
}

export interface BaseRow {
  // `LineRow` doesn't track base_material at the top level (it tracks
  // productCode and base_material_description per hour). Optional here so
  // both KitchenRow and LineRow can satisfy the constraint.
  base_material?: string[]
  base_material_description: string[]
  value: number
  cleaning_start_time: number
  cleaning_end_time: number
  preclean_start_time: number
  preclean_end_time: number
  changeover_start_time: number
  changeover_end_time: number
  slot_types: string[]
  // Optional to match KitchenRow / LineRow shapes — `addAdditionalSlots`
  // sets these only on hours that fall inside a downtime window.
  down_time_start_time?: number
  down_time_end_time?: number
}

export type CleaningRow = {
  base_material: string[]
  base_material_description: string[]
  value: number
  cleaning_start_time: number
  cleaning_end_time: number
  preclean_start_time: number
  preclean_end_time: number
  changeover_start_time: number
  changeover_end_time: number
  slot_types: string[]
  down_time_start_time?: number
  down_time_end_time?: number
}

// Inherits BaseRow's cleaning_/preclean_*_time so it satisfies the BaseRow
// constraint on shared helpers (`addAdditionalSlotsForCleaning`, `addSlots`).
// Those fields are unused on changeover rows — only changeover_*_time matters.
/** One changeover event, described for display.
 *
 *  `from`/`to` are populated only for a bottle-format change, whose `reason`
 *  the engine emits as `format_change:FROM->TO (RESOURCE)` — see
 *  `changeover_events_to_json` in scheduler_rust/src/postprocess.rs and the
 *  push site in simplified_decoder.rs. Every other changeover kind (equipment
 *  switch, asset changeover) carries free text, which is kept verbatim in
 *  `raw` so the box can still say something rather than nothing.
 */
export type ChangeoverDetail = {
  /** The line or kitchen the change is on — the row's `resource`. */
  resource: string
  from?: string
  to?: string
  /** Resource the change holds while it runs, when it names one. */
  heldResource?: string
  raw: string
  cost?: number
  start: number
  end: number
}

export type ChangeoverRow = BaseRow & {
  changeover_start_time: number
  changeover_end_time: number
  /** Every event touching this hour. Plural because two changeovers can
   *  land in one hour, and because the scalar start/end pair above keeps
   *  only the last writer's window. */
  changeover_details: ChangeoverDetail[]
}

export interface ColumnGroupingModalInterface {
  groupId: string
  headerName: string
  children: {
    field: string
    headerName: string
    width?: number
    headerClassName: string
    renderHeader: () => JSX.Element
  }[]
}

export interface DayGroupInterface {
  groupId: string
  headerName: string
  children: TimeSlotColumnInterface[]
}

export interface TimeSlotColumnInterface {
  field: string
  valueGetter: (
    params: unknown,
    row: Record<string, Record<string, number>>
  ) => number
  headerName: string
  width: number
  headerClassName: string
  renderHeader: () => JSX.Element
}
